import type { IdentitySettingsDto, IdentityTestDto } from '@dbrain/shared';
import { Badge, Button, EmptyState, Skeleton, TextInput } from '@dbrain/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client.js';
import { DataTable, type Column } from '../../components/admin/DataTable.js';
import { ErrorAlert } from '../../components/ErrorAlert.js';
import { formatCount } from '@dbrain/app-core/format';
import { SettingGroup } from '../../components/settings/rows.js';
import { AdminLayout } from './AdminLayout.js';

/**
 * Yönetim → "Kimlik doğrulama" (Phase 13d, 06 §22.2): the connection to the
 * company's authentication service as configured (read-only), a test that
 * sends no credentials, and the names the domains' workspaces show.
 */
const identityKey = ['admin', 'identity'] as const;

function ConnectionGroup({ provider }: { provider: NonNullable<IdentitySettingsDto['provider']> }) {
  const { t } = useTranslation();
  const test = useMutation({ mutationFn: () => api<IdentityTestDto>('POST', '/api/admin/identity/test') });
  const unchecked = <span className="muted">{t('identity.connection.unchecked')}</span>;
  const rows: [string, React.ReactNode][] = [
    [t('identity.connection.kind'), t('identity.connection.companyAuth')],
    [t('identity.connection.address'), <code key="url">{provider.baseUrl}</code>],
    [t('identity.connection.issuer'), provider.issuer ? <code key="iss">{provider.issuer}</code> : unchecked],
    [t('identity.connection.audience'), provider.audience ? <code key="aud">{provider.audience}</code> : unchecked],
    [t('identity.connection.ownerRole'), <code key="owner">{provider.superadminRole}</code>],
    [t('identity.connection.adminRole'), <code key="admin">{provider.adminRole}</code>],
    [t('identity.connection.roleFields'), provider.roleFields.join(', ')],
    [t('identity.connection.domainFields'), `${provider.domainFields.join(', ')} (${t('identity.connection.prefix', { prefix: provider.domainPrefix })})`],
    [t('identity.connection.fallback'), provider.fallbackAdmins > 0 ? t('identity.connection.fallbackOn', { count: provider.fallbackAdmins }) : t('identity.connection.fallbackOff')],
  ];
  return (
    <SettingGroup title={t('identity.connection.title')} hint={t('identity.connection.hint')} data-testid="identity-connection">
      <dl className="identity-facts">
        {rows.map(([label, value]) => (
          <div key={label} className="identity-facts__row">
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      <div className="row">
        <Button variant="secondary" busy={test.isPending} onClick={() => test.mutate()} data-testid="identity-test">
          {t('identity.connection.test')}
        </Button>
        {test.data ? (
          <span role="status" data-testid="identity-test-result">
            <Badge tone={test.data.ok ? 'success' : 'danger'}>{t(`identity.connection.result.${test.data.result}`)}</Badge>{' '}
            <span className="muted">
              {test.data.status !== null ? t('identity.connection.answer', { status: test.data.status, ms: test.data.latencyMs }) : t('identity.connection.noAnswer', { ms: test.data.latencyMs })}
            </span>
          </span>
        ) : null}
      </div>
      <ErrorAlert error={test.error} />
    </SettingGroup>
  );
}

type DomainDto = IdentitySettingsDto['domains'][number];

/** The name of a domain's shared workspace (and team): empty takes the directory's label. */
function DomainName({ domain }: { domain: DomainDto }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => api('PUT', `/api/admin/identity/domains/${domain.id}`, { displayName: name?.trim() ? name.trim() : null }),
    onSuccess: () => {
      setName(null);
      void queryClient.invalidateQueries({ queryKey: identityKey });
      void queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    },
  });
  return (
    <>
      <form
        className="member-budget"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <TextInput
          aria-label={t('identity.domains.displayName', { domain: domain.name })}
          value={name ?? domain.displayName ?? ''}
          placeholder={domain.defaultName}
          maxLength={120}
          onChange={(e) => setName(e.target.value)}
          data-testid="identity-domain-name"
        />
        <Button variant="secondary" busy={save.isPending} disabled={name === null} type="submit">
          {t('common.save')}
        </Button>
      </form>
      <ErrorAlert error={save.error} />
    </>
  );
}

export function IdentityContent() {
  const { t, i18n } = useTranslation();
  const settings = useQuery({ queryKey: identityKey, queryFn: () => api<IdentitySettingsDto>('GET', '/api/admin/identity') });
  if (settings.isPending) return <Skeleton block />;
  if (settings.error) return <ErrorAlert error={settings.error} />;
  const { provider, domains } = settings.data;
  if (!provider) return <EmptyState>{t('identity.none')}</EmptyState>;
  const columns: Column<DomainDto>[] = [
    { id: 'domain', header: t('identity.domains.domain'), width: 'md', cell: (d) => <code>{d.name}</code> },
    { id: 'workspace', header: t('identity.domains.workspace'), cell: (d) => <DomainName domain={d} /> },
    { id: 'members', header: t('identity.domains.members'), width: 'xs', numeric: true, cell: (d) => formatCount(d.members, i18n.language) },
    { id: 'admins', header: t('identity.domains.admins'), width: 'sm', numeric: true, cell: (d) => formatCount(d.admins, i18n.language) },
  ];
  return (
    <div className="settings-groups" data-testid="settings-identity">
      <ConnectionGroup provider={provider} />
      <SettingGroup title={t('identity.domains.title')} hint={t('identity.domains.hint')} data-testid="identity-domains">
        {domains.length === 0 ? (
          <EmptyState>{t('identity.domains.empty')}</EmptyState>
        ) : (
          <DataTable label={t('identity.domains.title')} columns={columns} rows={domains} rowKey={(d) => d.id} rowTestId={(d) => `identity-domain-${d.name}`} />
        )}
      </SettingGroup>
    </div>
  );
}

/** Yönetim → Kimlik doğrulama, as a page of its own (D-100). */
export function IdentityPage() {
  const { t } = useTranslation();
  return (
    <AdminLayout section="identity" title={t('admin.sections.identity')}>
      <IdentityContent />
    </AdminLayout>
  );
}
